<template>
  <div
    class="product-listing flex flex-col items-center gap-10 justify-center"
    :class="productListingDisplay"
    data-testid="ProductListingProductCardsList"
  >
    <!-- TODO - add image alt text if provided by typesense -->
    <!-- TODO - remove default 0 value for price once it is provided by typesense -->
    <LazyVisibleProductCard
      v-for="item in transformedProducts"
      :key="item.objectID"
      data-testid="productListingProductCardsListItem"
      :title="item.name ?? ''"
      :full-product-price="item.fullProductPrice || 0"
      :discounted-price="item.discountedPrice"
      :last30-days-price="item.last30DaysPrice ?? undefined"
      :image="item.image"
      :link="item.url ?? ''"
      :tags="item.tags"
      :brand="item.brand"
      :sku="item.objectID ?? ''"
      :stock-count="item.stockCount"
      :rating="item.avgRating"
      :reviews-count="item.reviewsCount"
      :price-guarantee-url="priceGuaranteeUrl"
      :dimensions="getDimensions(item)"
      :is-freight="item.isPackageTypeFreight"
      :parcel-delivery-date="parcelDeliveryDate"
      :freight-delivery-date="freightDeliveryDate"
      :key-facts="attributesLocalized ? attributesLocalized.highlights : []"
      :release-date="item.releaseDate || ''"
      :energy-rating="getEnergyRating({ item })"
      :currency="item.priceCurrency ?? ''"
      :categories="item.categories ?? {}"
      :product-box-type-tag="productBoxClickLocation"
      hydrate-on-visible
      @product-clicked="() => onProductClicked(item)"
      @title-hovered="(productSku) => getProductAttributes(item.url ?? '', productSku)"
      @title-leave="() => cancelDataFetching()"
    />
  </div>
</template>

<script setup lang="ts">
import type { HitResultItem } from 'vue-instantsearch/vue3/es';
import { SEARCH_VIEW } from '~~/types/search/SearchView';
import { convertTypesenseProductsToCardProducts } from '~~/helpers/typesense/convertTypesenseProductsToCardProducts';
import type { ProductCardItem } from '~~/types/product/ProductCardItem';
import type ProductDimensionsInterface from '~~/types/product/attribute/ProductDimensions';
import { getEnergyRating } from '~~/helpers/product/energyRating';

defineOptions({ name: 'ProductListing' });

const props = withDefaults(defineProps<{
  items?: HitResultItem[];
  listType?: string;
  query?: string;
}>(), {
  items: () => [],
  listType: SEARCH_VIEW.Grid,
  query: '',
});

const emit = defineEmits<{
  (e: 'product-listing-shown', items: HitResultItem[]): void;
}>();

const route = useRoute();
const { query, items } = toRefs(props);
const { isOnSearchPage } = useIsPage();
const { callViewHitsEvent, callClickHitEvent } = useTypesenseGtmEvents();
const { priceGuaranteeUrl } = useRouteBestPriceGuarantee();
const { search: searchProductDeliveryDate, parcelDeliveryDate, freightDeliveryDate } = useProductDeliveryDate(true);
const currentSlug = Array.isArray(route.params.slug) ? route.params.slug[0] : (route.params.slug || '');
const { callViewItemListEvent } = useViewItemListEvent(items.value, {
  searchTerm: query.value, currentSlug,
});

onMounted(() => {

  emit('product-listing-shown', props.items);
  callViewItemListEvent();

  if (isOnSearchPage.value) {

    callViewHitsEvent(props.items);

  }

});

const productListingDisplay = computed(() => {

  if (props.listType === SEARCH_VIEW.Grid) {

    return 'grid lg:grid';

  } else {

    return 'grid lg:flex list';

  }

});

const onProductClicked = (product: HitResultItem) => {

  if (isOnSearchPage.value) {

    callClickHitEvent(product, props.query);

  }

};

const getDimensions = (item: ProductCardItem): ProductDimensionsInterface => {

  return {
    height: item.height,
    width: item.width,
    length: item.length,
    hasDimensions: item.hasDimensions,
  };

};

const transformedProducts = computed(() => convertTypesenseProductsToCardProducts(props.items));

const {
  search: searchProduct,
  masterProductLocalisedAttributes: attributesLocalized,
  masterProduct: product,
} = useProductForListing();

const timeout = ref<ReturnType<typeof setTimeout> | null>(null);

const getProductAttributes = (link: string, sku: string) => {

  if (product.value && product.value.sku === sku) {

    return;

  }

  const searchParams = {
    slug: link.slice(1),
  };

  clearTimeout(timeout.value ?? undefined);
  timeout.value = setTimeout(async () => {

    await searchProduct(searchParams);

  }, 300);

};

const cancelDataFetching = () => {

  clearTimeout(timeout.value ?? undefined);

};

const { productBoxClickLocation } = useProductBoxClickLocation();

useLazyAsyncData(`parcel-freight-delivery-date`, async () => {

  await searchProductDeliveryDate();

  return '';

});
</script>

<style lang="scss">
@use "~/assets/scss/helpers/_breakpoints" as breakpoints;
$list-first-column: 200px;
$list-first-row-height: 50px;
$full-row: 1 / span 2;

.product-listing {
  grid-template-columns: repeat(auto-fill, var(--product-card-width));
  padding: 0 20px;

  &.list {
    @include breakpoints.for-desktop {
      .product-card {
        height: var(--product-card-list-height);
        width: 100%;
        padding: 0;

        a {
          height: 100%;
          display: grid;
          // defining the width of the 3 rows that appears in the list view
          grid-template-columns: repeat(auto-fill, $list-first-column calc(75% - #{$list-first-column}) 25%);
          grid-template-rows: $list-first-row-height;
        }

        .product-card__image {
          grid-column: 1;
          grid-row: $full-row;
          height: unset;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          padding: 0 var(--spacer-sm) 0 var(--spacer-xs);

          img {
            height: var(--product-card-list-height);
            padding-bottom: 2px;
          }
        }

        .product-card__top {
          grid-column: 2;
          grid-row: 1;
        }

        .product-card__name {
          grid-column: 2;
          grid-row: 2;
          text-align: left;
          padding-bottom: var(--spacer-sm);
        }

        .product-card__pricing {
          grid-column: 3;
          grid-row: $full-row;
          height: unset;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding-right: var(--spacer-xl);
        }
      }
    }
  }
}
</style>
